import { FunctionComponent, ReactNode, useMemo } from "react";

import { useIsFeatureActive } from "@/utils/useIsFeatureActive";

import { HelpLauncher } from "@/components/HelpCenter/HelpLauncher";

import { useGetLeanListQuery } from "@/pages/Private/redux/list/list.api";

import { ListActionLinks } from "@/pages/Private/pages/List/components/ListActionLinks";
import { listHasCampaigns } from "@/pages/Private/pages/List/utils/listHasCampaigns";
import { CustomListsAutocompleteForSequencerItem } from "@/pages/Private/pages/List/components/CustomListsAutocompleteForSequencer";
import { List } from "@/pages/Private/pages/List/schema/list";
import { FeaturesCodes } from "@/pages/Private/pages/Users/enum/codes";

export interface ListContextHeaderProps {
	/** Page title shown on the left (e.g. "Review to send", "Inbox", "Outbox"). */
	title: string;
	/** The relocated list filter element (CustomListsAutocompleteForSequencer). */
	listFilter: ReactNode;
	/** Currently selected lists from the list filter. */
	selectedLists: CustomListsAutocompleteForSequencerItem[];
	/** Lean lists already loaded by the page, used to resolve the single selection. */
	lists: List[];
}

/**
 * Shared top header for the Review/Inbox/Outbox surfaces: page title, the list
 * filter shown next to it, and — only when exactly one list is selected — the
 * list-action buttons (Sources / Find Person / Campaigns / Analytics / Settings),
 * mirroring the Unassigned list page header.
 */
export const ListContextHeader: FunctionComponent<ListContextHeaderProps> = ({
	title,
	listFilter,
	selectedLists,
	lists,
}) => {
	const isSequencerActive = useIsFeatureActive(FeaturesCodes.SEQUENCER);

	const isSingleSelection = selectedLists.length === 1;

	const selectedListId = isSingleSelection ? Number(selectedLists[0].id) : undefined;

	const loadedDetail = useMemo(
		() => (selectedListId ? lists.find((list) => list.id === selectedListId) : undefined),
		[lists, selectedListId]
	);

	// Fallback when the selected list isn't in the page already loaded into memory.
	const { data: fetchedDetail } = useGetLeanListQuery(
		{ id: selectedListId ?? 0 },
		{ skip: !selectedListId || !!loadedDetail }
	);

	const detail = loadedDetail ?? fetchedDetail;

	return (
		<div className="flex items-center gap-2.5 h-auto py-2 px-4 bg-white border-b border-border-light">
			<span className="text-sm font-semibold text-text whitespace-nowrap">{title}</span>

			{listFilter}

			<div className="flex-1" />

			{isSingleSelection && detail && (
				<ListActionLinks
					entityType={detail.entityType}
					hasCampaigns={listHasCampaigns(detail)}
					id={String(detail.id)}
					isSequencerActive={isSequencerActive}
					scrapeLevel={detail.scrapeLevel}
					sourceType={detail.sourceType}
					specialType={detail.specialType}
					variant="compact"
				/>
			)}

			<HelpLauncher />
		</div>
	);
};
